import { supabase, supabaseUrl, supabaseAnonKey } from "../lib/supabase";

/**
 * Usage tracking — records which pages and modals people use, and for how long.
 *
 * Events are appended to `app_usage_events` (insert-only for users, readable by admins).
 * A "view" (page or modal) produces rows sharing a view_id:
 *   page_view / modal_open   → one row, written immediately (use these for counts)
 *   page_leave / modal_close → one or more rows carrying duration_ms = ACTIVE time only
 *                              (tab visible and the user not idle), so a tab left open
 *                              overnight doesn't count as 12 hours of use.
 *                              SUM them per view — a row is written each time the tab
 *                              is hidden (checkpoint) and when the view ends.
 *
 * Why checkpoint on hide: `visibilitychange → hidden` is the last event browsers
 * reliably deliver. pagehide is skipped on browser quit, crashes, sleep, and some
 * window closes, so time is banked whenever the tab is hidden and a lost pagehide
 * costs nothing.
 *
 * Events are batched and flushed every few seconds, when the tab is hidden, and on
 * unload (via fetch keepalive so the final batch survives the page closing).
 *
 * Disabled in development unless REACT_APP_USAGE_TRACKING=true.
 */

const TABLE = "app_usage_events";
const APP = "react";
const FLUSH_INTERVAL_MS = 10000;
const TICK_MS = 5000;
const IDLE_AFTER_MS = 5 * 60 * 1000;
const MAX_BATCH = 50;
const MAX_QUEUE = 500;
const ID_SEGMENT =
  /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{20,})$/i;

const enabled =
  typeof window !== "undefined" &&
  (process.env.NODE_ENV === "production" ||
    process.env.REACT_APP_USAGE_TRACKING === "true");

let initialized = false;
let disabled = !enabled;
let accessToken = null;
let queue = [];
let flushing = false;
let lastTick = Date.now();
let lastActivityAt = Date.now();
const openViews = new Map();

const uuid = () =>
  window.crypto?.randomUUID?.() ??
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });

const getSessionId = () => {
  try {
    let id = sessionStorage.getItem("usage_session_id");
    if (!id) {
      id = uuid();
      sessionStorage.setItem("usage_session_id", id);
    }
    return id;
  } catch {
    return null;
  }
};

/** Collapse record ids so /projects/startup/16146 and /projects/startup/16150 group together. */
export const normalizePath = (pathname = "/") =>
  "/" +
  pathname
    .split("/")
    .filter(Boolean)
    .map((segment) => (ID_SEGMENT.test(segment) ? ":id" : segment))
    .join("/");

const isActive = (now) =>
  document.visibilityState === "visible" &&
  now - lastActivityAt < IDLE_AFTER_MS;

// Credit elapsed active time to every open view. Gaps are capped so a throttled
// background timer or a sleeping laptop can't credit a big chunk at once.
const tick = () => {
  const now = Date.now();
  const elapsed = Math.min(now - lastTick, TICK_MS * 2);
  lastTick = now;
  if (!isActive(now)) return;
  openViews.forEach((view) => {
    view.activeMs += elapsed;
  });
};

const enqueue = (event) => {
  if (disabled) return;
  queue.push({
    app: APP,
    session_id: getSessionId(),
    path: window.location.pathname,
    ...event,
  });
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
  if (queue.length >= MAX_BATCH) flush();
};

const send = (batch, { keepalive = false } = {}) =>
  fetch(`${supabaseUrl}/rest/v1/${TABLE}`, {
    method: "POST",
    keepalive,
    headers: {
      apikey: supabaseAnonKey,
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
      Prefer: "return=minimal",
    },
    body: JSON.stringify(batch),
  });

export const flush = async ({ keepalive = false } = {}) => {
  if (disabled || !queue.length || !accessToken) return;
  if (keepalive) {
    // Page may be going away: fire-and-forget. Browsers cap in-flight keepalive
    // bodies at 64KB, so send a few small chunks rather than one big one.
    for (let i = 0; i < 3 && queue.length; i++) {
      send(queue.splice(0, MAX_BATCH), { keepalive: true }).catch(() => {});
    }
    return;
  }
  if (flushing) return;
  const batch = queue.splice(0, queue.length);
  flushing = true;
  try {
    const res = await send(batch);
    if (res.ok) return;
    if (res.status === 401) {
      // Token expired between refreshes — retry once AuthContext refreshes it
      queue = [...batch, ...queue].slice(-MAX_QUEUE);
    } else {
      // Table missing / RLS rejection / bad payload: retrying won't help, stop for this page load
      disabled = true;
      queue = [];
      console.warn(
        `[usage] tracking disabled (${res.status}): ${await res.text()}`,
      );
    }
  } catch {
    queue = [...batch, ...queue].slice(-MAX_QUEUE); // network blip — retry next flush
  } finally {
    flushing = false;
  }
};

/** Start a page or modal view. Returns a view id to pass to endView. */
export const startView = (kind, name, props) => {
  if (disabled || !name) return null;
  tick();
  const viewId = uuid();
  openViews.set(viewId, {
    kind,
    name,
    path: window.location.pathname,
    activeMs: 0,
    startedAt: Date.now(),
  });
  enqueue({
    event_type: kind === "page" ? "page_view" : "modal_open",
    name,
    view_id: viewId,
    ...(props && { props }),
  });
  return viewId;
};

// Write the active time accrued since the last leave row and reset the counter.
const enqueueLeave = (viewId, view) => {
  const now = Date.now();
  enqueue({
    event_type: view.kind === "page" ? "page_leave" : "modal_close",
    name: view.name,
    path: view.path,
    view_id: viewId,
    duration_ms: Math.round(view.activeMs),
    props: { wall_ms: now - view.startedAt },
  });
  view.activeMs = 0;
  view.startedAt = now;
  view.checkpointed = true;
};

export const endView = (viewId) => {
  if (!viewId) return;
  tick();
  const view = openViews.get(viewId);
  if (!view) return;
  openViews.delete(viewId);
  // Already banked on hide and nothing new since — no need for a 0ms row
  if (view.checkpointed && view.activeMs === 0) return;
  enqueueLeave(viewId, view);
};

// Bank active time for every open view without ending it (see header comment)
const checkpointViews = () => {
  tick();
  openViews.forEach((view, viewId) => {
    if (view.activeMs > 0) enqueueLeave(viewId, view);
  });
};

/** One-off action, e.g. trackAction("export_pdf", { projectId }) */
export const trackAction = (name, props) => {
  enqueue({ event_type: "action", name, ...(props && { props }) });
};

/* ---- Reporting (admin-only via RLS) ---- */

/** Per page/modal/action totals since a date, optionally for one person. */
export const fetchUsageSummary = (since, userEmail = null) =>
  supabase.rpc("get_app_usage_summary", {
    p_since: since,
    p_user_email: userEmail,
  });

/** Per-person totals since a date. */
export const fetchUsageByUser = (since) =>
  supabase.rpc("get_app_usage_by_user", { p_since: since });

export const initUsageTracking = () => {
  if (initialized || disabled) return;
  initialized = true;

  supabase.auth.onAuthStateChange((_event, session) => {
    accessToken = session?.access_token ?? null;
  });

  const markActive = () => {
    lastActivityAt = Date.now();
  };
  ["pointerdown", "pointermove", "keydown", "wheel", "scroll"].forEach((type) =>
    window.addEventListener(type, markActive, { passive: true, capture: true }),
  );

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      checkpointViews();
      flush({ keepalive: true });
    } else {
      lastTick = Date.now(); // don't credit time spent hidden
      markActive();
    }
  });

  // Page is closing: close out every open view and send what's left
  window.addEventListener("pagehide", () => {
    [...openViews.keys()].forEach(endView);
    flush({ keepalive: true });
  });

  setInterval(tick, TICK_MS);
  setInterval(() => flush(), FLUSH_INTERVAL_MS);
};
